/**
 * PATH: packages/ui/src/components/notification-center/useNotifications.ts
 * FILE_ID: UI-NOTIFCENTER-USENOTIFICATIONS-001
 * PURPOSE: Hook to fetch, manage, and poll notifications from the unified API
 *          plus, when an org is selected, the workforce-api feed (#1237), as
 *          ONE list with ONE unread count.
 * WHY: Provides real-time notification data to NotificationCenter and Header badge
 * FLOW:
 *   apiClient -> GET /api/notifications/ -> list
 *   apiClient -> GET /api/notifications/stats/ -> unread counts
 *   apiClient -> GET <workforce>/notifications?company_id= -> wf: rows (best-effort)
 *   apiClient -> POST /api/notifications/<id>/read/ | <workforce>/notifications/<id>/read
 *   apiClient -> POST /api/notifications/read-all/ (+ workforce read-all)
 *   apiClient -> POST /api/notifications/<id>/dismiss/ (workforce rows: read + drop)
 *   Polls every 15s only while the tab is visible; a return after a missed
 *   tick refreshes at once (change #1307).
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import type { NotificationItem } from './types'
import {
  isWorkforceId,
  mapWorkforceItems,
  mergeFeeds,
  mergedUnread,
  resolveWorkforceApiBase,
  workforceFeedUrl,
  workforceReadUrl,
} from './workforceFeed'

const POLL_INTERVAL_MS = 15_000
const EMPTY: NotificationItem[] = []

export interface UseNotificationsOptions {
  /** Selected Django organisation id; enables the workforce feed. */
  workforceOrgId?: string | number | null
}

export function useNotifications(
  apiClient?: {
    get: (url: string) => Promise<any>
    post: (url: string, data: any) => Promise<any>
  },
  options: UseNotificationsOptions = {},
) {
  const { workforceOrgId = null } = options
  const [djangoItems, setDjangoItems] = useState<NotificationItem[]>(EMPTY)
  const [workforceItems, setWorkforceItems] = useState<NotificationItem[]>(EMPTY)
  const [isLoading, setIsLoading] = useState(false)
  const [djangoUnread, setDjangoUnread] = useState(0)
  const isMounted = useRef(true)
  // BUG-2026-07-15-MEMBERS-FETCH-STORM (sibling hardening): the 15s
  // interval fired regardless of whether the previous poll had settled,
  // so a stalled backend (or a saturated browser connection pool) piled
  // up concurrent GET /api/notifications/ requests and amplified the
  // very saturation that stalled them. One poll in flight at a time;
  // a tick that finds one pending is skipped, not queued.
  const inFlight = useRef(false)
  const lastFetchAt = useRef(0)
  const wfBase = useMemo(
    () => resolveWorkforceApiBase((import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}),
    [],
  )

  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  const fetchStats = useCallback(async () => {
    if (!apiClient) return
    try {
      const response = await apiClient.get('/api/notifications/stats/')
      const data = response.data || response
      if (isMounted.current) {
        setDjangoUnread(data.total_unread ?? 0)
      }
    } catch {
      // stats fail silently — badge just shows stale count
    }
  }, [apiClient])

  // Best-effort: a user with no workforce membership gets 4xx here; the bell
  // then shows Django rows only, never an error.
  const fetchWorkforce = useCallback(async () => {
    if (!apiClient) return
    const url = workforceFeedUrl(wfBase, workforceOrgId)
    if (!url) {
      if (isMounted.current) setWorkforceItems(EMPTY)
      return
    }
    try {
      const response = await apiClient.get(url)
      const data = response.data || response
      const rows = Array.isArray(data?.notifications) ? data.notifications : []
      if (isMounted.current) setWorkforceItems(mapWorkforceItems(rows))
    } catch {
      // keep whatever we had; a transient workforce outage must not blank the bell
    }
  }, [apiClient, wfBase, workforceOrgId])

  const fetchNotifications = useCallback(async () => {
    if (!apiClient) return
    if (inFlight.current) return
    inFlight.current = true
    lastFetchAt.current = Date.now()

    setIsLoading(true)
    try {
      const [listRes] = await Promise.all([
        apiClient.get('/api/notifications/?page_size=50'),
        fetchStats(),
        fetchWorkforce(),
      ])
      const data = listRes.data || listRes
      const items: NotificationItem[] = data.results || []
      if (isMounted.current) setDjangoItems(items)
    } catch (error) {
      if (import.meta.env.DEV) console.error('Failed to fetch notifications:', error)
    } finally {
      inFlight.current = false
      if (isMounted.current) setIsLoading(false)
    }
  }, [apiClient, fetchStats, fetchWorkforce])

  const markWorkforceRead = useCallback(
    (id: string) =>
      setWorkforceItems((prev) =>
        prev.map((n) =>
          String(n.id) === id ? { ...n, read_at: new Date().toISOString(), is_read: true } : n
        )
      ),
    [],
  )

  const markAsRead = useCallback(
    async (id: string) => {
      if (!apiClient) return
      try {
        const wfUrl = workforceReadUrl(wfBase, workforceOrgId, id)
        if (wfUrl) {
          await apiClient.post(wfUrl, {})
          markWorkforceRead(id)
          return
        }
        await apiClient.post(`/api/notifications/${id}/read/`, {})
        setDjangoItems((prev) =>
          prev.map((n) =>
            String(n.id) === id ? { ...n, read_at: new Date().toISOString(), is_read: true } : n
          )
        )
        setDjangoUnread((prev) => Math.max(0, prev - 1))
      } catch (error) {
        console.error('Failed to mark notification as read:', error)
      }
    },
    [apiClient, markWorkforceRead, wfBase, workforceOrgId]
  )

  const readAllWorkforce = useCallback(async () => {
    if (!apiClient || workforceOrgId == null) return
    const url = `${wfBase}/notifications/read-all?company_id=${encodeURIComponent(String(workforceOrgId))}`
    try {
      await apiClient.post(url, {})
    } catch {
      // best-effort; the local list is still marked read below
    }
  }, [apiClient, wfBase, workforceOrgId])

  const markAllAsRead = useCallback(async () => {
    if (!apiClient) return
    try {
      await Promise.all([apiClient.post('/api/notifications/read-all/', {}), readAllWorkforce()])
      const now = new Date().toISOString()
      setDjangoItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || now, is_read: true })))
      setWorkforceItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || now, is_read: true })))
      setDjangoUnread(0)
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error)
    }
  }, [apiClient, readAllWorkforce])

  const dismiss = useCallback(
    async (id: string) => {
      if (!apiClient) {
        setDjangoItems((prev) => prev.filter((n) => String(n.id) !== id))
        setWorkforceItems((prev) => prev.filter((n) => String(n.id) !== id))
        return
      }
      try {
        if (isWorkforceId(id)) {
          // workforce has no dismiss; read it so it stops counting, then hide it
          const wfUrl = workforceReadUrl(wfBase, workforceOrgId, id)
          if (wfUrl) await apiClient.post(wfUrl, {})
          setWorkforceItems((prev) => prev.filter((n) => String(n.id) !== id))
          return
        }
        await apiClient.post(`/api/notifications/${id}/dismiss/`, {})
        setDjangoItems((prev) => prev.filter((n) => String(n.id) !== id))
        setDjangoUnread((prev) => Math.max(0, prev - 1))
      } catch (error) {
        if (import.meta.env.DEV) console.error('Failed to dismiss notification:', error)
      }
    },
    [apiClient, wfBase, workforceOrgId]
  )

  const dismissAll = useCallback(async () => {
    if (!apiClient) {
      setDjangoItems(EMPTY)
      setWorkforceItems(EMPTY)
      setDjangoUnread(0)
      return
    }
    try {
      await Promise.all([apiClient.post('/api/notifications/dismiss-all/', {}), readAllWorkforce()])
      setDjangoItems(EMPTY)
      setWorkforceItems(EMPTY)
      setDjangoUnread(0)
    } catch (error) {
      if (import.meta.env.DEV) console.error('Failed to dismiss all notifications:', error)
    }
  }, [apiClient, readAllWorkforce])

  useEffect(() => { fetchNotifications() }, [fetchNotifications])

  useEffect(() => {
    if (!apiClient) return
    const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden'
    const interval = setInterval(() => {
      if (!hidden()) fetchNotifications()
    }, POLL_INTERVAL_MS)
    const onVisibility = () => {
      if (!hidden() && Date.now() - lastFetchAt.current >= POLL_INTERVAL_MS) fetchNotifications()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [apiClient, fetchNotifications])

  const notifications = useMemo(() => mergeFeeds(djangoItems, workforceItems), [djangoItems, workforceItems])
  const unreadCount = useMemo(() => mergedUnread(djangoUnread, workforceItems), [djangoUnread, workforceItems])

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    dismiss,
    dismissAll,
    refresh: fetchNotifications,
  }
}
